import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { useNavigate } from "react-router-dom";

import { RewardProgressBar } from "@/modules/onboarding/components/RewardProgressBar/RewardProgressBar";
import { get } from "api";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import CheckSmall from "atoms/icon/icons/ic_check_small_ol.svg";
import QrCode from "atoms/icon/icons/qr_code.svg";
import UserNav from "atoms/icon/icons/user_nav.svg";
import { formatCurrency } from "toolbox/Money";
import pluralize from "utils/pluralize";

export type RewardProgressProps = {
  rewardsCount: number;
  nextMilestone: number;
  unlockProgressPercent: number;
  cashToUnlock?: number;
  marketplaceEnabled?: boolean;
  visitCashValue?: number;
  displayGreetings?: boolean;
  spendBasedRewardsEnabled: boolean;
};

export const RewardProgress = ({
  rewardsCount,
  nextMilestone,
  unlockProgressPercent,
  cashToUnlock,
  marketplaceEnabled,
  visitCashValue,
  displayGreetings,
  spendBasedRewardsEnabled,
}: RewardProgressProps) => {
  const navigate = useNavigate();
  const { data: userCanCheckIn } = useQuery({
    queryKey: ["/user/check-in"],
    queryFn: () => get("/user/check-in"),
  });

  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  if (!user) {
    return null;
  }

  const userMessage = (): string => {
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const greetings =
      currentHour < 12
        ? "Good Morning"
        : currentHour < 18
        ? "Good Afternoon"
        : "Good Evening";
    if (user.firstName) {
      return `${greetings}, ${user.firstName}`.toUpperCase();
    }
    return greetings;
  };

  const getTitleText = (): string | undefined => {
    if (user.isGuest) {
      return "Unlock exclusive rewards—join now!";
    }

    if (nextMilestone === 0 && rewardsCount === 0) {
      return undefined;
    }

    if (
      !marketplaceEnabled ||
      visitCashValue === 0 ||
      !spendBasedRewardsEnabled
    ) {
      return `Only ${nextMilestone} ${pluralize(
        nextMilestone,
        "visit",
        "visits"
      )} until your next reward!`;
    }

    if (nextMilestone) {
      return `Only ${nextMilestone} more ${pluralize(
        nextMilestone,
        "visit",
        "visits"
      )} ${
        cashToUnlock && `or spend ${formatCurrency(Math.ceil(cashToUnlock))}`
      } for your next reward!`;
    }

    if (rewardsCount > 0) {
      return `You have ${rewardsCount} reward${
        rewardsCount > 1 ? "s" : ""
      } waiting!`;
    }

    return undefined;
  };

  const getTitle = () => {
    const titleText = getTitleText();
    if (!titleText) {
      return null;
    }
    return (
      <>
        <div className="mb-2 w-full bg-transparent">
          <RewardProgressBar progress={unlockProgressPercent} />
        </div>
        {displayGreetings ? (
          <h3 className="bg-transparent font-serif text-h3 text-primary">
            {titleText}
          </h3>
        ) : (
          <h2 className="bg-transparent font-serif text-h2 text-primary">
            {titleText}
          </h2>
        )}
      </>
    );
  };

  const getSubtitle = () => {
    const shopEnabled =
      marketplaceEnabled &&
      visitCashValue &&
      visitCashValue > 0 &&
      spendBasedRewardsEnabled;

    if (user.isGuest) {
      return `Sign up to start earning rewards${
        shopEnabled ? " and access exclusive offers." : ""
      }.`;
    }

    return `Scan in store${
      shopEnabled ? " or shop in-app" : ""
    } to get closer to your next reward.`;
  };

  const YouHaveScannedBanner = () => (
    <div className="flex items-center rounded bg-background-two py-2 pl-2 pr-4">
      <div className="mb-1">
        <Icon svg={CheckSmall} color="secondaryText" size="x2small" />
      </div>
      <div className="ml-2 text-body2 text-secondary">
        Thanks for visiting! You&apos;ve checked-in today.
      </div>
    </div>
  );

  const ScanInButton = () => {
    const handleClick = () =>
      navigate(user.isGuest ? "/signin" : "/scan-checkin-page");
    const icon = user.isGuest ? UserNav : QrCode;
    const hasNextReward = nextMilestone > 0 || rewardsCount > 0;
    const buttonText = user.isGuest
      ? "Sign up to start earning"
      : hasNextReward
      ? "Check in for rewards"
      : "Check in";

    return (
      <Button startIconSVG={icon} onClick={handleClick} size="xlarge">
        {buttonText}
      </Button>
    );
  };

  const titleText = getTitleText();
  const shouldHideProgress =
    nextMilestone === 0 && rewardsCount === 0 && !user.isGuest;

  return (
    <div
      className={cx(
        "-mb-4 flex flex-col items-center rounded-b-xl px-5 text-center",
        displayGreetings ? "bg-transparent pb-5 pt-3" : "gap-3 pb-5 pt-3",
        shouldHideProgress && "pb-8"
      )}
    >
      {displayGreetings && (
        <div className="bg-transparent pb-2 font-sans text-sub1 text-brand-color">
          {userMessage()}
        </div>
      )}
      {getTitle()}
      {!shouldHideProgress && (
        <div
          className={cx(
            "bg-transparent font-sans leading-[22px] text-secondary",
            displayGreetings
              ? "pb-4 pt-2 text-body1"
              : "mt-1 text-base font-normal"
          )}
        >
          {getSubtitle()}
        </div>
      )}
      {userCanCheckIn ? <ScanInButton /> : <YouHaveScannedBanner />}
    </div>
  );
};
